import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { useMemo } from 'react';

import type { IncidentRow } from '@/api/types';
import { DataTable, EmptyState, type DataTableColumn } from '@/design-system';
import { spacing } from '@/theme/tokens';

import { SeverityBadge } from './SeverityBadge';
import { StateBadge } from './StateBadge';

const useStyles = makeStyles({
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: tokens.colorNeutralForeground2 },
  ruleName: { overflowWrap: 'anywhere', gap: spacing.xs, display: 'flex', flexDirection: 'column' },
  resource: { overflowWrap: 'anywhere' },
  ruleType: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  details: { overflowWrap: 'anywhere', display: 'block' },
});

function fmtMs(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

interface IncidentTableProps { incidents: IncidentRow[]; }

export function IncidentTable({ incidents }: IncidentTableProps) {
  const classes = useStyles();
  const columns = useMemo<readonly DataTableColumn<IncidentRow>[]>(() => [
    { id: 'severity', header: 'Severity', getValue: (item) => item.severity, renderCell: (item) => <SeverityBadge severity={item.severity} />, sortable: false },
    { id: 'state', header: 'State', getValue: (item) => item.alertState, renderCell: (item) => <StateBadge state={item.alertState} />, sortable: false },
    { id: 'rule', header: 'Alert rule', getValue: (item) => item.alertRuleName, renderCell: (item) => <div className={classes.ruleName}><Text size={200}>{item.alertRuleName}</Text><span className={classes.ruleType}>{item.monitorService}</span></div>, sortable: false, width: 280 },
    { id: 'resource', header: 'Resource', getValue: (item) => item.targetResourceName, renderCell: (item) => <div className={classes.resource}><Text size={200}>{item.targetResourceName}</Text>{item.targetResourceType ? <Text size={100} className={classes.mono} block>{item.targetResourceType.split('/').pop()}</Text> : null}</div>, sortable: false, width: 240 },
    { id: 'condition', header: 'Condition', getValue: (item) => item.monitorCondition, renderCell: (item) => <Text size={200}>{item.monitorCondition}</Text>, sortable: false },
    { id: 'details', header: 'Details', getValue: (item) => item.description, renderCell: (item) => <Text size={200} className={classes.details}>{item.description || '—'}</Text>, sortable: false, width: 320 },
    { id: 'fired', header: 'Fired', getValue: (item) => item.firedAtMs, renderCell: (item) => fmtMs(item.firedAtMs), sortable: false },
    { id: 'resolved', header: 'Resolved', getValue: (item) => item.resolvedAtMs, renderCell: (item) => fmtMs(item.resolvedAtMs), sortable: false },
  ], [classes]);

  return <DataTable ariaLabel="Incidents" columns={columns} getRowId={(item) => item.id} items={incidents} tableId="incidents" emptyState={<EmptyState title="No incidents" description="No incidents match the current filters." />} sortDescription="Incident order follows the server order. Sort is unavailable because this table loads one cursor page at a time." />;
}
